export interface Article {
  id: number | string;
  title: string;
  excerpt: string;
  content?: string;
  /** 表示・下位互換用。複数カテゴリ時は「・」結合を推奨 */
  category: string;
  /** 複数カテゴリ。未設定時は category のみを利用 */
  categories?: string[];
  tags: string[];
  author: string;
  publishedAt: string;
  publishDate?: string;
  readTime: string;
  image: string;
  status: string;
  layout: 'standard' | 'featured';
}

const ARTICLES_JSON_URL = '/articles.json';
const ARTICLES_INDEX_URL = '/articles-index.json';

/** プリレンダ HTML の head に埋め込まれた記事 JSON（CSR 起動直後の表示用） */
export function readArticleBootstrapFromDom(): Article | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById('social-trip-article-bootstrap');
  if (!el?.textContent?.trim()) return null;
  try {
    const data = JSON.parse(el.textContent) as Article;
    if (data?.id != null && data.status === '公開') {
      return data;
    }
  } catch {
    /* ignore */
  }
  return null;
}

// 記事データを取得
export const fetchArticles = async (): Promise<Article[]> => {
  try {
    const response = await fetch(ARTICLES_JSON_URL);
    if (!response.ok) {
      throw new Error('記事データの取得に失敗しました');
    }
    const articles = await response.json();
    return articles;
  } catch (error) {
    console.error('記事の取得エラー:', error);
    return [];
  }
};

// 記事を保存（ローカルストレージとGitHubに保存）
export const saveArticles = async (articles: Article[]): Promise<boolean> => {
  try {
    // ローカルストレージに保存（即座の反映用）
    localStorage.setItem('articles', JSON.stringify(articles));

    // Cloudinaryに保存するAPIを呼び出し（バックグラウンド処理）
    saveToCloudinary(articles).catch(error => {
      console.warn('Cloudinary保存は失敗しましたが、ローカルには保存されました:', error);
    });

    return true;
  } catch (error) {
    console.error('記事の保存エラー:', error);
    return false;
  }
};

// Cloudinaryに記事を保存（バックエンドAPI経由）
const saveToCloudinary = async (articles: Article[]): Promise<void> => {
  try {
    const response = await fetch('/.netlify/functions/save-articles-cloudinary', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ articles }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Cloudinary保存APIエラー: ${errorData.message || response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ Cloudinaryに保存されました:', result.message);
  } catch (error) {
    console.error('Cloudinary保存エラー:', error);
    throw error;
  }
};

// Cloudinaryから記事を取得（管理画面用）
const fetchArticlesFromCloudinary = async (): Promise<Article[]> => {
  try {
    const response = await fetch('/.netlify/functions/save-articles-cloudinary', {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Cloudinaryからの記事取得に失敗しました');
    }

    const articles = await response.json();
    console.log('getArticles: Fetched from Cloudinary:', articles.length);
    return articles;
  } catch (error) {
    console.error('Cloudinary取得エラー:', error);
    return [];
  }
};

/**
 * 本番サイト表示用。軽量 index → 旧 articles.json の順で取得。
 * Cloudinary Function は公開ページでは呼ばない（往復・待ち時間の削減）。
 */
async function fetchPublishedArticlesUncached(): Promise<Article[]> {
  try {
    const res = await fetch(ARTICLES_INDEX_URL);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch (error) {
    console.warn('[articleApi] articles-index.json の取得に失敗、articles.json にフォールバック', error);
  }
  return fetchArticles();
}

/** SPA セッション内の重複取得を防ぐ（記事詳細で同一データを複数回取らない） */
let publishedArticlesSessionCache: Article[] | undefined;
let publishedArticlesInflight: Promise<Article[]> | null = null;

const publishedArticleByIdCache = new Map<string, Article>();
const publishedArticleByIdInflight = new Map<string, Promise<Article | null>>();

/**
 * 本番用記事一覧（content なし index）。同一タブ内では1回取得した結果を再利用する。
 */
export const fetchPublishedArticles = async (): Promise<Article[]> => {
  if (publishedArticlesSessionCache !== undefined) {
    return publishedArticlesSessionCache;
  }
  if (publishedArticlesInflight) {
    return publishedArticlesInflight;
  }
  publishedArticlesInflight = fetchPublishedArticlesUncached()
    .then((data) => {
      // 空配列はキャッシュしない（初回だけネットが失敗して [] になると、リロードまで記事が出ない）
      if (Array.isArray(data) && data.length > 0) {
        publishedArticlesSessionCache = data;
      }
      publishedArticlesInflight = null;
      return data;
    })
    .catch((err) => {
      publishedArticlesInflight = null;
      throw err;
    });
  return publishedArticlesInflight;
};

/** 公開記事1件を個別 JSON から取得（詳細ページ用） */
export async function fetchPublishedArticleById(id: number | string): Promise<Article | null> {
  const key = String(id);
  const cached = publishedArticleByIdCache.get(key);
  if (cached) {
    return cached;
  }
  const inflight = publishedArticleByIdInflight.get(key);
  if (inflight) {
    return inflight;
  }

  const promise = (async (): Promise<Article | null> => {
    try {
      const res = await fetch(`/articles/${key}.json`);
      if (res.ok) {
        const article = (await res.json()) as Article;
        if (article?.status === '公開') {
          publishedArticleByIdCache.set(key, article);
          return article;
        }
        return null;
      }
    } catch (error) {
      console.warn(`[articleApi] /articles/${key}.json の取得に失敗`, error);
    }
    const articles = await fetchPublishedArticles();
    return pickPublishedArticleById(articles, id);
  })().finally(() => {
    publishedArticleByIdInflight.delete(key);
  });

  publishedArticleByIdInflight.set(key, promise);
  return promise;
}

/** テスト・デバッグ用: セッションキャッシュを捨てる */
export function clearPublishedArticlesSessionCache(): void {
  publishedArticlesSessionCache = undefined;
  publishedArticlesInflight = null;
  publishedArticleByIdCache.clear();
  publishedArticleByIdInflight.clear();
}

/** 取得済み一覧から公開記事1件を取り出す（全件再取得を避けるため） */
export function pickPublishedArticleById(
  articles: Article[],
  id: number | string
): Article | null {
  const found = articles.find((a) => String(a.id) === String(id));
  if (!found || found.status !== '公開') {
    return null;
  }
  return found;
}

// 管理画面・編集用（優先順位: ローカルストレージ → Cloudinary → JSONファイル）
export const getArticles = async (): Promise<Article[]> => {
  try {
    // まずローカルストレージを確認
    const localArticles = localStorage.getItem('articles');
    if (localArticles) {
      console.log('getArticles: Found localStorage data');
      const parsed = JSON.parse(localArticles);
      console.log('getArticles: localStorage articles:', parsed.length);
      // 空の配列の場合は、Cloudinaryから再取得
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }

    // ローカルストレージになければCloudinaryから取得を試みる
    console.log('getArticles: Trying to fetch from Cloudinary');
    const cloudinaryArticles = await fetchArticlesFromCloudinary();
    if (cloudinaryArticles.length > 0) {
      // Cloudinaryから取得したデータをローカルストレージに保存
      localStorage.setItem('articles', JSON.stringify(cloudinaryArticles));
      return cloudinaryArticles;
    }

    // CloudinaryにもなければローカルのJSONファイルから取得
    console.log('getArticles: Fetching from /articles.json');
    const articles = await fetchArticles();
    console.log('getArticles: Fetched articles:', articles.length);

    // JSONファイルから取得したデータをローカルストレージに保存（初回起動時の初期化）
    if (articles.length > 0) {
      localStorage.setItem('articles', JSON.stringify(articles));
    }

    return articles;
  } catch (error) {
    console.error('記事の取得エラー:', error);
    // エラー時もJSONファイルから取得を試みる
    try {
      const articles = await fetchArticles();
      if (articles.length > 0) {
        localStorage.setItem('articles', JSON.stringify(articles));
      }
      return articles;
    } catch (fetchError) {
      console.error('JSONファイルからの取得も失敗:', fetchError);
      return [];
    }
  }
};

// 記事を作成
export const createArticle = async (article: Omit<Article, 'id'>): Promise<Article> => {
  const articles = await getArticles();

  // 新しいIDを生成（既存の最大ID + 1）
  const maxId = articles.reduce((max, a) => Math.max(max, Number(a.id)), 0);
  const newArticle: Article = {
    ...article,
    id: maxId + 1
  };

  // 記事を追加
  articles.unshift(newArticle);

  // 保存
  await saveArticles(articles);

  return newArticle;
};

// 記事を更新
export const updateArticle = async (id: number | string, updatedArticle: Partial<Article>): Promise<boolean> => {
  const articles = await getArticles();
  const index = articles.findIndex(a => a.id == id);

  if (index === -1) {
    return false;
  }

  // 記事を更新
  articles[index] = {
    ...articles[index],
    ...updatedArticle
  };

  // 保存
  return await saveArticles(articles);
};

// 記事を削除
export const deleteArticle = async (id: number | string): Promise<boolean> => {
  const articles = await getArticles();
  const filteredArticles = articles.filter(a => a.id != id);

  if (filteredArticles.length === articles.length) {
    // 削除対象が見つからなかった
    return false;
  }

  // 保存
  return await saveArticles(filteredArticles);
};

// IDで記事を取得（管理画面・下書き含む一覧から）
export const getArticleById = async (id: number | string): Promise<Article | null> => {
  const articles = await getArticles();
  return articles.find(a => a.id == id) || null;
};

/** 公開サイトの記事詳細用。サーバー由来データのみ参照し、非公開は返さない */
export const getPublishedArticleById = fetchPublishedArticleById;

// ローカルストレージをクリアしてJSONファイルからリセット
export const resetArticles = async (): Promise<boolean> => {
  try {
    localStorage.removeItem('articles');
    return true;
  } catch (error) {
    console.error('記事のリセットエラー:', error);
    return false;
  }
};
